import { XMAX_SDK_BUILD_REGION, type XmaxSdkRegion } from '../build-config'
import { version } from '../../package.json'

export type XmaxProbeOptions = {
  /** Healthy-session sampling rate. Abnormal sessions are always retained. @default 1 */
  samplingRate?: number
  /** Sliding RTC statistics window. @default 10000 */
  windowDurationMs?: number
  /** Promote a slow first-frame session to full retention. Omit until the first P95 is known. */
  e2eThresholdMs?: number
  /** Enable passive RTC statistics collection. @default true */
  rtcStatsEnabled?: boolean
}

export type ResolvedXmaxProbeConfig = {
  samplingRate: number
  windowDurationMs: number
  e2eThresholdMs?: number
  rtcStatsEnabled: boolean
}

type RemoteProbeConfig = {
  sampling_rate?: unknown
  window_duration_ms?: unknown
  e2e_threshold_ms?: unknown
  rtc_stats_enabled?: unknown
}

export const XMAX_TELEMETRY_CONFIG_ENDPOINTS: Record<XmaxSdkRegion, string> = {
  cn: 'https://log.xmaxai.com/v1/probe/config',
  global: 'https://api.xmax.cloud/v1/probe/config',
}

export const XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT = XMAX_TELEMETRY_CONFIG_ENDPOINTS[XMAX_SDK_BUILD_REGION]
const REFRESH_MS = 5 * 60_000
const FETCH_TIMEOUT_MS = 3_000
const DEFAULT_CONFIG: ResolvedXmaxProbeConfig = {
  samplingRate: 1,
  windowDurationMs: 10_000,
  rtcStatsEnabled: true,
}

let remoteConfig: Partial<ResolvedXmaxProbeConfig> = {}
let endpoint = XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT
let runtimeEnv = 'prod'
let refreshTimer: ReturnType<typeof setTimeout> | undefined
let fetching = false

function configured(value: string | undefined): value is string {
  if (!value || value.includes('__pending_')) return false
  try { return new URL(value).protocol === 'https:' } catch { return false }
}

function finiteRange(value: unknown, min: number, max: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : undefined
}

function parseRemote(value: RemoteProbeConfig): Partial<ResolvedXmaxProbeConfig> {
  const parsed: Partial<ResolvedXmaxProbeConfig> = {}
  const samplingRate = finiteRange(value.sampling_rate, 0, 1)
  const windowDurationMs = finiteRange(value.window_duration_ms, 1_000, 60_000)
  const e2eThresholdMs = finiteRange(value.e2e_threshold_ms, 1_000, 10 * 60_000)
  if (samplingRate !== undefined) parsed.samplingRate = samplingRate
  if (windowDurationMs !== undefined) parsed.windowDurationMs = windowDurationMs
  if (e2eThresholdMs !== undefined) parsed.e2eThresholdMs = e2eThresholdMs
  if (typeof value.rtc_stats_enabled === 'boolean') parsed.rtcStatsEnabled = value.rtc_stats_enabled
  return parsed
}

function timer(callback: () => void, ms: number): ReturnType<typeof setTimeout> {
  const handle = setTimeout(callback, ms)
  ;(handle as unknown as { unref?: () => void }).unref?.()
  return handle
}

function scheduleRefresh(): void {
  if (!configured(endpoint) || refreshTimer) return
  refreshTimer = timer(() => {
    refreshTimer = undefined
    void refreshRemoteProbeConfig()
  }, REFRESH_MS)
}

async function refreshRemoteProbeConfig(): Promise<void> {
  if (!configured(endpoint) || fetching) return
  fetching = true
  const controller = new AbortController()
  const timeout = timer(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const target = new URL(endpoint)
    target.searchParams.set('env', runtimeEnv)
    target.searchParams.set('sdk_version', version)
    const response = await fetch(target.href, { method: 'GET', credentials: 'omit', signal: controller.signal })
    if (!response.ok) return
    const value = await response.json() as RemoteProbeConfig
    if (value && typeof value === 'object') remoteConfig = parseRemote(value)
  } catch {
    // Fail-silent and retain the last valid config.
  } finally {
    clearTimeout(timeout)
    fetching = false
    scheduleRefresh()
  }
}

/** Starts or stops the fail-silent remote config loop. */
export function configureRemoteProbeConfig(value: string | false | undefined, env = 'prod'): void {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = undefined
  endpoint = value === false ? '' : (value ?? XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT)
  runtimeEnv = ['prod', 'test', 'dev'].includes(env) ? env : 'prod'
  if (configured(endpoint)) void refreshRemoteProbeConfig()
}

export function resolveProbeConfig(local: XmaxProbeOptions | undefined): ResolvedXmaxProbeConfig {
  const localConfig: Partial<ResolvedXmaxProbeConfig> = {}
  const samplingRate = finiteRange(local?.samplingRate, 0, 1)
  const windowDurationMs = finiteRange(local?.windowDurationMs, 1_000, 60_000)
  const e2eThresholdMs = finiteRange(local?.e2eThresholdMs, 1_000, 10 * 60_000)
  if (samplingRate !== undefined) localConfig.samplingRate = samplingRate
  if (windowDurationMs !== undefined) localConfig.windowDurationMs = windowDurationMs
  if (e2eThresholdMs !== undefined) localConfig.e2eThresholdMs = e2eThresholdMs
  if (typeof local?.rtcStatsEnabled === 'boolean') localConfig.rtcStatsEnabled = local.rtcStatsEnabled
  return { ...DEFAULT_CONFIG, ...localConfig, ...remoteConfig }
}
